import React, { useState, useEffect } from "react";
import { ArrowLeft, MapPin, Camera, CheckCircle, AlertCircle, X, Clock, Phone } from "lucide-react";

// Color themes matching map pin colors
export const FORM_THEMES = {
  ci: {
    key: "ci",
    label: "CI",
    color: "#FFD700",
    bg: "bg-yellow-500",
    bgGradient: "from-yellow-500 to-yellow-600",
    bgHover: "hover:bg-yellow-600",
    bgLight: "bg-yellow-50",
    border: "border-yellow-500",
    borderLight: "border-yellow-200",
    text: "text-yellow-600",
    textDark: "text-yellow-700",
    btnClass: "bg-yellow-500 hover:bg-yellow-600",
    photoBorderHover: "hover:border-yellow-400 hover:bg-yellow-50",
    nextLabel: "Next: DTM",
    nextPath: (dtId) => `/dtm/${dtId}`,
  },
  dtm: {
    key: "dtm",
    label: "DTM",
    color: "#FFA500",
    bg: "bg-orange-500",
    bgGradient: "from-orange-500 to-orange-600",
    bgHover: "hover:bg-orange-600",
    bgLight: "bg-orange-50",
    border: "border-orange-500",
    borderLight: "border-orange-200",
    text: "text-orange-600",
    textDark: "text-orange-700",
    btnClass: "bg-orange-500 hover:bg-orange-600",
    photoBorderHover: "hover:border-orange-400 hover:bg-orange-50",
    nextLabel: "Next: DTC",
    nextPath: (dtId) => `/dtc/${dtId}`,
  },
  dtc: {
    key: "dtc",
    label: "DTC",
    color: "#800080",
    bg: "bg-purple-700",
    bgGradient: "from-purple-700 to-purple-800",
    bgHover: "hover:bg-purple-800",
    bgLight: "bg-purple-50",
    border: "border-purple-700",
    borderLight: "border-purple-200",
    text: "text-purple-700",
    textDark: "text-purple-800",
    btnClass: "bg-purple-700 hover:bg-purple-800",
    photoBorderHover: "hover:border-purple-400 hover:bg-purple-50",
    nextLabel: "Next: MI",
    nextPath: (dtId) => `/mi/${dtId}`,
  },
  mi: {
    key: "mi",
    label: "MI",
    color: "#008000",
    bg: "bg-green-700",
    bgGradient: "from-green-700 to-green-800",
    bgHover: "hover:bg-green-800",
    bgLight: "bg-green-50",
    border: "border-green-700",
    borderLight: "border-green-200",
    text: "text-green-700",
    textDark: "text-green-800",
    btnClass: "bg-green-700 hover:bg-green-800",
    photoBorderHover: "hover:border-green-400 hover:bg-green-50",
    nextLabel: null,
    nextPath: null,
  },
};

// Pill toggle button
export const Pill = ({ selected, onClick, children, color = "blue", testId }) => {
  const colors = {
    blue: selected ? "border-blue-500 bg-blue-500 text-white" : "border-slate-200 bg-white text-slate-600",
    green: selected ? "border-green-500 bg-green-50 text-green-700" : "border-slate-200 bg-white text-slate-500",
    red: selected ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 bg-white text-slate-500",
    purple: selected ? "border-purple-500 bg-purple-500 text-white" : "border-slate-200 bg-white text-slate-600",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-full font-bold border-2 px-3 py-1.5 text-xs transition-colors ${colors[color]}`}>
      {children}
    </button>
  );
};

// Colored header with 3-column layout
export const FormHeader = ({ theme, dt, gpsStatus, gpsData, onGpsClick, onBack }) => (
  <header className={`sticky top-0 z-20 bg-gradient-to-r ${theme.bgGradient} px-3 py-3`} data-testid={`${theme.key}-header`}>
    <div className="flex items-center gap-3">
      {/* Left: Back + Big Label */}
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={onBack} className="text-white/80 p-1" data-testid={`${theme.key}-back-btn`}>
          <ArrowLeft size={20} />
        </button>
        <span className="text-2xl font-black text-white tracking-tight">{theme.label}</span>
      </div>

      {/* Center: DT Code + Feeder */}
      <div className="flex-1 min-w-0 text-center">
        {dt && <div className="font-mono text-base font-bold text-white truncate">{dt.dt_code}</div>}
        {dt && <div className="text-[10px] text-white/70 truncate">{dt.feeder_name || dt.feeder_code}</div>}
      </div>

      {/* Right: GPS pill */}
      <button
        onClick={gpsStatus !== "captured" ? onGpsClick : undefined}
        className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-bold shrink-0 ${
          gpsStatus === "captured" ? "bg-green-400/90 text-green-900" :
          gpsStatus === "capturing" ? "bg-white/30 text-white animate-pulse" :
          gpsStatus === "error" ? "bg-red-400/90 text-red-900" : "bg-white/30 text-white"
        }`}
        data-testid="gps-status-pill"
      >
        <MapPin size={11} />
        {gpsStatus === "captured" ? "GPS" :
         gpsStatus === "capturing" ? "..." :
         gpsStatus === "error" ? "Retry" : "GPS"}
      </button>
    </div>
  </header>
);

// Basic shutdown info display (read-only) — for MI form
export const ShutdownInfo = ({ shutdowns }) => {
  if (!shutdowns || shutdowns.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
        <Clock size={13} className="text-slate-400" />
        <span className="text-[11px] text-slate-400">No shutdown scheduled for today/tomorrow</span>
      </div>
    );
  }
  return (
    <div className="space-y-1">
      {shutdowns.map(sd => {
        let timeStr = "—";
        let dayLabel = "";
        try {
          const start = new Date(sd.start_datetime);
          const end = new Date(sd.end_datetime);
          timeStr = `${start.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} - ${end.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
          dayLabel = sd.availability === "now" ? "Today" : "Tomorrow";
        } catch { /* ignore */ }
        return (
          <div key={sd.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${sd.availability === "now" ? "bg-green-500 animate-pulse" : "bg-blue-400"}`} />
            <Clock size={11} className="text-slate-400 shrink-0" />
            <span className="text-[11px] text-slate-600 font-medium">{timeStr}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
              sd.availability === "now" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-600"
            }`}>{dayLabel}</span>
          </div>
        );
      })}
    </div>
  );
};

// Enhanced shutdown info with JE/Lineman contacts + countdown timer — for DTM & DTC
export const EnhancedShutdownInfo = ({ shutdowns }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  if (!shutdowns || shutdowns.length === 0) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg">
        <Clock size={13} className="text-slate-400" />
        <span className="text-[11px] text-slate-400">No shutdown scheduled for today/tomorrow</span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {shutdowns.map(sd => {
        let timeStr = "—";
        let dayLabel = "";
        let endDate = null;
        let isEnded = false;
        let remainingMs = 0;
        try {
          const start = new Date(sd.start_datetime);
          endDate = new Date(sd.end_datetime);
          timeStr = `${start.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} - ${endDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}`;
          dayLabel = sd.availability === "now" ? "Today" : "Tomorrow";
          if (sd.availability === "now") {
            remainingMs = endDate.getTime() - now.getTime();
            isEnded = remainingMs <= 0;
          }
        } catch { /* ignore */ }

        const remainH = Math.floor(remainingMs / 3600000);
        const remainM = Math.floor((remainingMs % 3600000) / 60000);
        const timerStr = isEnded ? "ENDED" : `${String(remainH).padStart(2, "0")}:${String(remainM).padStart(2, "0")}`;

        return (
          <div key={sd.id} className="flex items-stretch bg-slate-50 border border-slate-200 rounded-lg overflow-hidden" data-testid={`shutdown-card-${sd.id}`}>
            {/* Left: Details */}
            <div className="flex-1 px-3 py-2 space-y-1">
              <div className="flex items-center gap-2">
                <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${sd.availability === "now" ? "bg-green-500 animate-pulse" : "bg-blue-400"}`} />
                <Clock size={11} className="text-slate-400 shrink-0" />
                <span className="text-[11px] text-slate-600 font-medium">{timeStr}</span>
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                  sd.availability === "now" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-600"
                }`}>{dayLabel}</span>
              </div>
              {/* JE + Lineman row */}
              <div className="flex items-center gap-3 flex-wrap">
                {sd.je_name && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-500">JE:</span>
                    <span className="text-[10px] text-slate-700 font-medium">{sd.je_name}</span>
                    {sd.je_contact && (
                      <a href={`tel:${sd.je_contact}`} className="text-blue-500 p-0.5" data-testid="call-je">
                        <Phone size={10} />
                      </a>
                    )}
                  </div>
                )}
                {sd.lineman_name && (
                  <div className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-500">LM:</span>
                    <span className="text-[10px] text-slate-700 font-medium">{sd.lineman_name}</span>
                    {sd.lineman_contact && (
                      <a href={`tel:${sd.lineman_contact}`} className="text-blue-500 p-0.5" data-testid="call-lineman">
                        <Phone size={10} />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right: Big Timer */}
            {sd.availability === "now" && (
              <div className={`flex flex-col items-center justify-center px-3 min-w-[70px] ${
                isEnded ? "bg-red-100" : "bg-green-50"
              }`}>
                <span className={`text-lg font-black tabular-nums ${isEnded ? "text-red-600" : "text-slate-800"}`}>
                  {timerStr}
                </span>
                <span className={`text-[8px] font-bold uppercase ${isEnded ? "text-red-500" : "text-slate-400"}`}>
                  {isEnded ? "" : "left"}
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Photo grid (3 columns with labels)
export const PhotoGrid = ({ photos, photoLabels, onUpload, onRemove, submitted, theme, testIdPrefix }) => {
  const capturedCount = photos.filter(p => p).length;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-bold text-slate-600 uppercase">Photos *</label>
        <span className="text-[10px] text-slate-400">{capturedCount}/3 captured</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {photoLabels.map((label, i) => (
          <div key={label} className="relative">
            {photos[i] ? (
              <div className="border-2 border-green-400 rounded-lg overflow-hidden bg-green-50">
                <div className="relative aspect-square">
                  <img src={photos[i].uri} alt={label} className="w-full h-full object-cover" />
                  {!submitted && (
                    <button onClick={() => onRemove(i)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-0.5">
                      <X size={10} />
                    </button>
                  )}
                </div>
                <div className="py-1 text-center bg-green-600 text-white text-[9px] font-semibold">{label}</div>
              </div>
            ) : (
              <label className={`flex flex-col items-center justify-center aspect-square border-2 border-dashed border-slate-300 rounded-lg cursor-pointer ${theme.photoBorderHover} transition-all ${submitted ? "opacity-60 pointer-events-none" : ""}`}>
                <input type="file" accept="image/*" capture="environment" className="hidden"
                  onChange={e => onUpload(e, i)} disabled={submitted} data-testid={`${testIdPrefix}-photo-${i + 1}`} />
                <Camera size={24} className="text-slate-300 mb-1" />
                <span className="text-[10px] text-slate-400 font-medium">{label}</span>
              </label>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Error banner
export const ErrorBanner = ({ error, extraHint, testId }) => {
  if (!error) return null;
  return (
    <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3" data-testid={testId}>
      <div className="flex items-start gap-2">
        <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <div className="font-bold text-red-700 text-sm">Submission Failed</div>
          <div className="text-xs text-red-600 mt-1">{error}</div>
          {extraHint && <div className="mt-2 text-[10px] text-red-500 bg-red-100 p-2 rounded">{extraHint}</div>}
        </div>
      </div>
    </div>
  );
};

// Bottom bar with Submit + Next
export const FormBottomBar = ({ theme, submitted, loading, disabled, onSubmit, dtId, navigate }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-3 py-2 z-10">
    <div className="flex gap-2">
      <button onClick={onSubmit} disabled={submitted || loading || disabled}
        className={`flex-1 h-11 text-white rounded-lg text-sm font-bold disabled:opacity-40 shadow-md ${
          submitted ? "bg-slate-400 cursor-default" : `${theme.btnClass}`
        }`}
        data-testid={`${theme.key}-submit-btn`}>
        {loading ? "Submitting..." : submitted ? `${theme.label} Done` : `Complete ${theme.label}`}
      </button>
      {theme.nextPath ? (
        <button
          onClick={() => submitted && navigate(theme.nextPath(dtId))}
          disabled={!submitted}
          className={`h-11 px-4 rounded-lg text-sm font-bold shadow-md flex items-center gap-1 transition-all ${
            submitted
              ? `${theme.btnClass} text-white`
              : "bg-slate-100 text-slate-300 cursor-not-allowed"
          }`}
          data-testid={`${theme.key}-next-btn`}>
          {theme.nextLabel} <span className="text-lg">&#8250;</span>
        </button>
      ) : (
        submitted && (
          <button onClick={() => navigate(`/dt-master/${dtId}`)}
            className="h-11 px-4 bg-slate-600 text-white rounded-lg text-sm font-bold shadow-md flex items-center gap-1"
            data-testid="mi-view-dt-btn">
            View DT <CheckCircle size={14} />
          </button>
        )
      )}
    </div>
  </div>
);

// GPS capture helper
export const captureGPS = (setGpsStatus, setGpsData, silent = false, toast) => {
  if (!navigator.geolocation) {
    if (!silent) toast.error("GPS not supported on this device");
    setGpsStatus("error");
    return;
  }
  setGpsStatus("capturing");
  navigator.geolocation.getCurrentPosition(
    pos => {
      setGpsData({ lat: pos.coords.latitude.toFixed(6), lng: pos.coords.longitude.toFixed(6) });
      setGpsStatus("captured");
      if (!silent) toast.success("GPS captured!");
    },
    (err) => {
      setGpsStatus("error");
      if (!silent) {
        if (err.code === 1) toast.error("Location permission denied. Please enable GPS in settings");
        else if (err.code === 2) toast.error("GPS signal unavailable. Move to an open area");
        else toast.error("GPS capture timed out. Please try again");
      }
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );
};

// Photo watermark + compress helper
export const processPhoto = (file, metadata) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const MAX_DIM = 1280;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
          w = Math.round(w * ratio); h = Math.round(h * ratio);
        }
        canvas.width = w; canvas.height = h;
        ctx.drawImage(img, 0, 0, w, h);
        const overlayH = Math.max(80, h * 0.12);
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(0, h - overlayH, w, overlayH);
        const fs = Math.max(12, Math.floor(w / 30));
        ctx.fillStyle = '#FFFFFF';
        ctx.font = `bold ${fs}px Arial, sans-serif`;
        const p = 10, lh = fs + 4;
        let y = h - overlayH + p + fs;
        ctx.fillText(`${metadata.capturedDate}  ${metadata.capturedTime}`, p, y); y += lh;
        ctx.fillText(`GPS: ${metadata.gps_lat}, ${metadata.gps_lng}`, p, y); y += lh;
        ctx.fillText(`${metadata.technicianName} | ${metadata.label}`, p, y);
        const uri = canvas.toDataURL('image/jpeg', 0.6);
        resolve({ uri, ...metadata, filename: file.name });
      };
      img.src = event.target.result;
    };
    reader.readAsDataURL(file);
  });
};
